import { Health } from '@capgo/capacitor-health';
import { App } from '@capacitor/app';
import { Preferences } from '@capacitor/preferences';
import { isNative, isAndroid } from './Platform';

/**
 * Thin wrapper around @capgo/capacitor-health so the rest of the app can call
 * `storeMindfulSession(...)` without caring about HealthKit vs Health Connect
 * differences.
 *
 * Mirrors the interface of the old Cordova HealthIntegration class
 * (`isAvailable`, `isAuthorized`, `requestAuthorization`, `storeData`) so the
 * existing Dashboard callsite migrates with minimal churn. Adds a pending-writes
 * queue so a session completed while permission was revoked or the device was
 * offline is replayed on the next app resume.
 */

const READ_TYPES = ['mindfulness'];
const WRITE_TYPES = ['mindfulness'];
const KEY_AUTHORIZED = 'health.authorized';
const KEY_PENDING = 'health.pendingWrites';
const KEY_WRITTEN_PREFIX = 'health.written:';

async function readPendingQueue() {
    const { value } = await Preferences.get({ key: KEY_PENDING });
    if (!value) return [];
    try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed : [];
    } catch (_) {
        return [];
    }
}

async function writePendingQueue(queue) {
    await Preferences.set({
        key: KEY_PENDING,
        value: JSON.stringify(queue)
    });
}

async function markWritten(sessionId) {
    if (!sessionId) return;
    await Preferences.set({ key: KEY_WRITTEN_PREFIX + sessionId, value: '1' });
}

async function hasWrittenSession(sessionId) {
    if (!sessionId) return false;
    const { value } = await Preferences.get({
        key: KEY_WRITTEN_PREFIX + sessionId
    });
    return value === '1';
}

let resumeHooked = false;

export default class HealthIntegration {
    constructor() {
        this.authorized = false;
        // Keep the old Cordova-compat flag around for callers that still read it.
        try {
            this.authorized =
                window.localStorage.getItem('health-authorized') === 'true';
        } catch (_) {
            this.authorized = false;
        }
        this._hookResume();
    }

    _hookResume() {
        if (resumeHooked || !isNative()) return;
        resumeHooked = true;
        App.addListener('resume', () => {
            this.flushPendingWrites().catch(() => {});
        });
    }

    /**
     * Does the current platform expose a native health store we can talk to?
     * Returns false on the web build, on iPad pre-iPadOS 17 (no HealthKit),
     * and on Android devices without Health Connect.
     */
    async isAvailable() {
        if (!isNative()) return false;
        try {
            const result = await Health.isAvailable();
            return !!result?.available;
        } catch (_) {
            return false;
        }
    }

    /**
     * Returns the per-permission auth status without prompting. Call before
     * showing a "connect" button so users who already granted permission
     * aren't prompted again.
     */
    async checkAuthorization() {
        if (!(await this.isAvailable())) {
            return {
                readAuthorized: [],
                readDenied: READ_TYPES,
                writeAuthorized: [],
                writeDenied: WRITE_TYPES
            };
        }
        try {
            return await Health.checkAuthorization({
                read: READ_TYPES,
                write: WRITE_TYPES
            });
        } catch (err) {
            console.warn('[health] checkAuthorization failed', err);
            return {
                readAuthorized: [],
                readDenied: READ_TYPES,
                writeAuthorized: [],
                writeDenied: WRITE_TYPES
            };
        }
    }

    /**
     * Legacy shim kept for Dashboard/index.vue compatibility. Reads the
     * cached authorisation flag from localStorage without prompting.
     */
    async isAuthorized() {
        const status = await this.checkAuthorization();
        const authed =
            status.writeAuthorized.includes('mindfulness') &&
            status.readAuthorized.length > 0;
        this.authorized = authed;
        try {
            window.localStorage.setItem('health-authorized', String(authed));
        } catch (_) {
            /* ignore */
        }
        return authed;
    }

    /**
     * Prompt the user for read/write access to the data types the app needs.
     * On Android this redirects to Health Connect's permission screen.
     */
    async requestAuthorization() {
        if (!(await this.isAvailable())) return false;
        try {
            const status = await Health.requestAuthorization({
                read: READ_TYPES,
                write: WRITE_TYPES
            });
            const authed = status.writeAuthorized.includes('mindfulness');
            this.authorized = authed;
            try {
                window.localStorage.setItem(
                    'health-authorized',
                    String(authed)
                );
            } catch (_) {
                /* ignore */
            }
            await Preferences.set({
                key: KEY_AUTHORIZED,
                value: String(authed)
            });
            return authed;
        } catch (err) {
            console.warn('[health] requestAuthorization failed', err);
            return false;
        }
    }

    /**
     * Write a completed meditation session to HealthKit / Health Connect.
     * Idempotent via a local `sessionId` guard so replaying a meditation
     * does not double-log.
     *
     * @param {Object} params
     * @param {string} params.sessionId - Stable identifier from the backend.
     * @param {Date|string} params.startDate - Session start.
     * @param {Date|string} params.endDate - Session end.
     * @param {number} [params.durationSeconds] - Optional override for the
     *   written value; falls back to (endDate - startDate) in seconds.
     * @param {Object} [params.metadata] - Free-form labels (session title,
     *   theme, etc) written alongside the sample where the native API
     *   supports metadata.
     */
    async storeMindfulSession({
        sessionId,
        startDate,
        endDate,
        durationSeconds,
        metadata = {}
    } = {}) {
        if (!sessionId) {
            console.warn(
                '[health] storeMindfulSession called without sessionId'
            );
            return { ok: false, reason: 'missing-sessionId' };
        }
        if (await hasWrittenSession(sessionId)) {
            return { ok: true, deduped: true };
        }
        const start = startDate ? new Date(startDate) : null;
        const end = endDate ? new Date(endDate) : new Date();
        if (!start || Number.isNaN(start.getTime())) {
            return { ok: false, reason: 'invalid-startDate' };
        }
        const seconds =
            durationSeconds && durationSeconds > 0
                ? durationSeconds
                : Math.max(
                      1,
                      Math.round((end.getTime() - start.getTime()) / 1000)
                  );
        const minutes = Math.max(1, Math.round(seconds / 60));

        if (!(await this.isAvailable())) {
            // Queue for later — will flush on next resume once the device
            // has a health store available (e.g. Health Connect installed).
            await this._enqueueWrite({
                sessionId,
                startDate: start.toISOString(),
                endDate: end.toISOString(),
                durationSeconds: seconds,
                metadata
            });
            return { ok: false, queued: true, reason: 'health-unavailable' };
        }

        try {
            await Health.saveSample({
                dataType: 'mindfulness',
                value: minutes,
                unit: 'minute',
                startDate: start.toISOString(),
                endDate: end.toISOString(),
                metadata: {
                    sessionId: String(sessionId),
                    ...Object.fromEntries(
                        Object.entries(metadata).map(([k, v]) => [k, String(v)])
                    )
                }
            });
            await markWritten(sessionId);
            return { ok: true };
        } catch (err) {
            console.warn('[health] saveSample failed, queueing', err);
            await this._enqueueWrite({
                sessionId,
                startDate: start.toISOString(),
                endDate: end.toISOString(),
                durationSeconds: seconds,
                metadata
            });
            return { ok: false, queued: true, error: err };
        }
    }

    /**
     * Cordova-compat wrapper: the old app called `storeData(value, id)`
     * where `value` was duration in seconds.
     */
    async storeData(value, id) {
        const end = new Date();
        const start = new Date(end.getTime() - (Number(value) || 0) * 1000);
        return this.storeMindfulSession({
            sessionId: String(id || Date.now()),
            startDate: start,
            endDate: end,
            durationSeconds: Number(value) || 0
        });
    }

    async _enqueueWrite(entry) {
        const queue = await readPendingQueue();
        // Dedupe by sessionId so a retry loop doesn't balloon the queue.
        const filtered = queue.filter(x => x.sessionId !== entry.sessionId);
        filtered.push(entry);
        await writePendingQueue(filtered);
    }

    async flushPendingWrites() {
        if (!(await this.isAvailable())) return { drained: 0 };
        const queue = await readPendingQueue();
        if (queue.length === 0) return { drained: 0 };
        const remaining = [];
        let drained = 0;
        for (const entry of queue) {
            if (await hasWrittenSession(entry.sessionId)) {
                drained += 1;
                continue;
            }
            try {
                const minutes = Math.max(
                    1,
                    Math.round((entry.durationSeconds || 60) / 60)
                );
                await Health.saveSample({
                    dataType: 'mindfulness',
                    value: minutes,
                    unit: 'minute',
                    startDate: entry.startDate,
                    endDate: entry.endDate,
                    metadata: {
                        sessionId: String(entry.sessionId),
                        ...(entry.metadata || {})
                    }
                });
                await markWritten(entry.sessionId);
                drained += 1;
            } catch (err) {
                console.warn('[health] flush retry failed', err);
                remaining.push(entry);
            }
        }
        await writePendingQueue(remaining);
        return { drained };
    }

    /**
     * Opens the Health Connect settings on Android so users can grant
     * permissions or install Health Connect if missing. iOS no-op — the
     * HealthKit permission screen is only reachable from iOS Settings.app.
     */
    async openHealthSettings() {
        if (!isAndroid()) return;
        try {
            await Health.openHealthConnectSettings();
        } catch (err) {
            console.warn('[health] openHealthConnectSettings failed', err);
        }
    }
}
